/**
 * Training Component - Card-based LMS video library
 *
 * Features:
 * - Horizontal scroll category pills (matching Leads section style)
 * - Card-based material grid with thumbnails
 * - Admin: Full CRUD + Upload
 * - Standard users: Read-only (view/stream only, NO download)
 * - Error boundaries & loading states
 * - Mobile-first responsive design
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  GraduationCap,
  PlayCircle,
  FileText,
  Video,
  File,
  Image,
  Loader2,
  Eye,
  Clock,
  MoreVertical,
  Pencil,
  Trash2,
  BookOpen,
  Search,
} from "lucide-react";
import { MaterialViewerDialog } from "@/components/training/MaterialViewerDialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAllTrainingMaterials,
  getTrainingMaterialsByCategory,
  incrementViewCount,
  deleteTrainingMaterial,
  formatFileSize,
  TrainingMaterial,
  TrainingCategory,
} from "@/services/trainingService";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { isAdminUser } from "@/lib/rbac";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

// ============================================================================
// CONSTANTS
// ============================================================================

const statusVariants: Record<string, "success" | "warning" | "info"> = {
  new: "success",
  popular: "warning",
  essential: "info",
};

const categoryConfig: {
  value: "all" | TrainingCategory;
  label: string;
  icon: React.ReactNode;
}[] = [
  { value: "all", label: "All", icon: <BookOpen className="h-3.5 w-3.5" /> },
  {
    value: "onboarding",
    label: "Onboarding",
    icon: <GraduationCap className="h-3.5 w-3.5" />,
  },
  {
    value: "product",
    label: "Product",
    icon: <FileText className="h-3.5 w-3.5" />,
  },
  {
    value: "sales",
    label: "Sales",
    icon: <PlayCircle className="h-3.5 w-3.5" />,
  },
  {
    value: "compliance",
    label: "Compliance",
    icon: <File className="h-3.5 w-3.5" />,
  },
  {
    value: "technical",
    label: "Technical",
    icon: <Video className="h-3.5 w-3.5" />,
  },
  {
    value: "general",
    label: "General",
    icon: <BookOpen className="h-3.5 w-3.5" />,
  },
];

// ============================================================================
// PURE HELPERS (module-level — no component state)
// ============================================================================

const getTypeIcon = (type: string, size: string = "h-5 w-5") => {
  switch (type) {
    case "video":
      return <Video className={size} />;
    case "pdf":
      return <FileText className={size} />;
    case "doc":
      return <FileText className={size} />;
    case "image":
      return <Image className={size} />;
    default:
      return <File className={size} />;
  }
};

const getTypeColor = (type: string) => {
  switch (type) {
    case "video":
      return "bg-blue-500/15 text-blue-600 border-blue-500/20";
    case "pdf":
      return "bg-red-500/15 text-red-600 border-red-500/20";
    case "doc":
      return "bg-indigo-500/15 text-indigo-600 border-indigo-500/20";
    case "image":
      return "bg-emerald-500/15 text-emerald-600 border-emerald-500/20";
    default:
      return "bg-gray-500/15 text-gray-600 border-gray-500/20";
  }
};

const getTypeLabel = (type: string) => {
  switch (type) {
    case "video":
      return "Video";
    case "pdf":
      return "PDF";
    case "doc":
      return "Document";
    case "image":
      return "Image";
    default:
      return "File";
  }
};

// ============================================================================
// MATERIAL PREVIEW
// ============================================================================

function MaterialPreview({ material }: { material: TrainingMaterial }) {
  const [imgError, setImgError] = useState(false);

  // Determine the best preview src:
  // • explicit thumbnailUrl (always wins)
  // • image type → fileUrl is the image itself
  const imageSrc = !imgError
    ? (material.thumbnailUrl ??
      (material.type === "image" ? material.fileUrl : null))
    : null;

  // ── Image / thumbnail preview ──────────────────────────────────────────────
  if (imageSrc) {
    return (
      <div className="relative h-36 sm:h-40 overflow-hidden bg-muted">
        <img
          src={imageSrc}
          alt={material.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={() => setImgError(true)}
        />
        <div className="absolute top-2 left-2">
          <Badge className={cn("text-xs border", getTypeColor(material.type))}>
            {getTypeIcon(material.type, "h-3 w-3 mr-1")}
            {getTypeLabel(material.type)}
          </Badge>
        </div>
        <div className="absolute top-2 right-2">
          <Badge
            variant={statusVariants[material.status]}
            className="text-xs capitalize"
          >
            {material.status}
          </Badge>
        </div>
      </div>
    );
  }

  // ── Video first-frame preview ──────────────────────────────────────────────
  if (material.type === "video" && material.fileUrl) {
    return (
      <div className="relative h-36 sm:h-40 overflow-hidden bg-black">
        <video
          src={material.fileUrl}
          preload="metadata"
          muted
          playsInline
          className="w-full h-full object-cover"
          onError={(e) => {
            // Hide broken video and let the fallback banner render on next paint
            (e.currentTarget as HTMLVideoElement).style.display = "none";
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="rounded-full bg-white/90 p-3 shadow-lg">
            <PlayCircle className="h-8 w-8 text-primary" />
          </div>
        </div>
        <div className="absolute top-2 left-2">
          <Badge className={cn("text-xs border", getTypeColor(material.type))}>
            {getTypeIcon(material.type, "h-3 w-3 mr-1")}
            {getTypeLabel(material.type)}
          </Badge>
        </div>
        <div className="absolute top-2 right-2">
          <Badge
            variant={statusVariants[material.status]}
            className="text-xs capitalize"
          >
            {material.status}
          </Badge>
        </div>
      </div>
    );
  }

  // ── Icon banner fallback (PDF, doc, other — or image after load error) ─────
  return (
    <div
      className={cn(
        "relative h-24 sm:h-28 flex items-center justify-center",
        material.type === "video"
          ? "bg-blue-500/10"
          : material.type === "pdf"
            ? "bg-red-500/10"
            : material.type === "image"
              ? "bg-emerald-500/10"
              : "bg-indigo-500/10",
      )}
    >
      <div
        className={cn(
          "rounded-xl p-3",
          material.type === "video"
            ? "bg-blue-500/20 text-blue-600"
            : material.type === "pdf"
              ? "bg-red-500/20 text-red-600"
              : material.type === "image"
                ? "bg-emerald-500/20 text-emerald-600"
                : "bg-indigo-500/20 text-indigo-600",
        )}
      >
        {getTypeIcon(material.type, "h-7 w-7")}
      </div>
      <div className="absolute top-2 left-2">
        <Badge className={cn("text-xs border", getTypeColor(material.type))}>
          {getTypeLabel(material.type)}
        </Badge>
      </div>
      <div className="absolute top-2 right-2">
        <Badge
          variant={statusVariants[material.status]}
          className="text-xs capitalize"
        >
          {material.status}
        </Badge>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

interface TrainingProps {
  onEditMaterial?: (material: TrainingMaterial) => void;
}

export function Training({ onEditMaterial }: TrainingProps) {
  const { user } = useAuth();
  const [materials, setMaterials] = useState<TrainingMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewerDialogOpen, setViewerDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] =
    useState<TrainingMaterial | null>(null);
  const [activeCategory, setActiveCategory] = useState<
    "all" | TrainingCategory
  >("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] =
    useState<TrainingMaterial | null>(null);
  const [deleting, setDeleting] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isAdmin = isAdminUser(user);

  // ---- Data loading ----
  const loadMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const data =
        activeCategory === "all"
          ? await getAllTrainingMaterials()
          : await getTrainingMaterialsByCategory(activeCategory);
      setMaterials(data);
    } catch (error) {
      console.error("Error loading training materials:", error);
      toast.error("Failed to load training materials");
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  // ---- Category scroll to active ----
  useEffect(() => {
    if (scrollContainerRef.current) {
      const selectedButton = scrollContainerRef.current.querySelector(
        `[data-category="${activeCategory}"]`,
      );
      if (selectedButton) {
        selectedButton.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeCategory]);

  // ---- Search filter ----
  const filteredMaterials = materials.filter((m) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.title.toLowerCase().includes(q) ||
      m.description?.toLowerCase().includes(q) ||
      m.category.toLowerCase().includes(q) ||
      m.type.toLowerCase().includes(q)
    );
  });

  // ---- Category counts ----
  const categoryCounts = materials.reduce(
    (acc, m) => {
      acc[m.category] = (acc[m.category] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  // ---- Handlers ----
  const handleView = async (material: TrainingMaterial) => {
    try {
      await incrementViewCount(material.id);
      setSelectedMaterial({
        ...material,
        viewCount: material.viewCount + 1,
      });
      setViewerDialogOpen(true);
    } catch (error) {
      console.error("Error viewing material:", error);
      toast.error("Failed to open material");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!materialToDelete) return;
    try {
      setDeleting(true);
      await deleteTrainingMaterial(materialToDelete.id);
      toast.success("Material deleted successfully");
      setDeleteDialogOpen(false);
      setMaterialToDelete(null);
      await loadMaterials();
    } catch (error) {
      console.error("Error deleting material:", error);
      toast.error("Failed to delete material");
    } finally {
      setDeleting(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-4">
      {/* ---- Search Bar ---- */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search training materials..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* ---- Category Pills - Horizontal scroll (Leads style) ---- */}
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto -mx-4 px-4 pb-2 no-scrollbar"
      >
        <div className="flex gap-2 min-w-max">
          {categoryConfig.map((cat) => {
            const count =
              cat.value === "all"
                ? materials.length
                : categoryCounts[cat.value] || 0;

            return (
              <button
                key={cat.value}
                data-category={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                className={cn(
                  "flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-all whitespace-nowrap",
                  activeCategory === cat.value
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                )}
              >
                {cat.icon}
                {cat.label}
                <Badge
                  variant="secondary"
                  className="bg-background/20 text-current text-xs ml-0.5"
                >
                  {count}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>

      {/* ---- Materials Grid ---- */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Loading training materials...
          </p>
        </div>
      ) : filteredMaterials.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-full bg-muted p-4">
                <GraduationCap className="h-10 w-10 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-base mb-1">
                  No materials found
                </h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  {searchQuery
                    ? `No results for "${searchQuery}". Try a different search term.`
                    : activeCategory === "all"
                      ? "No training materials available yet. Get started by uploading your first material."
                      : `No materials in this category yet.`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
          <AnimatePresence mode="popLayout">
            {filteredMaterials.map((material, index) => (
              <motion.div
                key={material.id}
                layout
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.04, duration: 0.25 }}
              >
                <Card
                  variant="interactive"
                  className="hover:shadow-lg transition-all duration-300 group overflow-hidden"
                  onClick={() => handleView(material)}
                >
                  <CardContent className="p-0">
                    {/* Thumbnail / Type Banner */}
                    <MaterialPreview material={material} />

                    {/* Content */}
                    <div className="p-3 sm:p-4">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <h3 className="font-semibold text-sm sm:text-base leading-tight line-clamp-2 flex-1">
                          {material.title}
                        </h3>
                        {isAdmin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              asChild
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0 -mt-0.5"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditMaterial?.(material);
                                }}
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMaterialToDelete(material);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>

                      {material.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                          {material.description}
                        </p>
                      )}

                      {/* Meta info */}
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {material.duration && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {material.duration}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {material.viewCount}{" "}
                          {material.viewCount === 1 ? "view" : "views"}
                        </span>
                        <span className="text-muted-foreground/60">
                          {formatFileSize(material.fileSize)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* ---- Material Viewer Dialog ---- */}
      <MaterialViewerDialog
        material={selectedMaterial}
        open={viewerDialogOpen}
        onOpenChange={setViewerDialogOpen}
        isAdmin={isAdmin}
      />

      {/* ---- Delete Confirmation Dialog ---- */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Training Material</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{materialToDelete?.title}"? This
              will permanently remove the file from storage. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
